import { Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { filter, take } from 'rxjs';
import { IMenu } from 'src/app/domain/IMenu';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { ToastService } from '../services/toast/toast.service';
import { BarService } from './bar.service';
import { DOCUMENT } from '@angular/common';
import { IUser } from 'src/app/domain/registration/models/IUser';
import { S3_URL_BASE } from 'src/environments/environment';
import { KeyboardService } from '../components/keyboard/keyboard.service';
import { IPartner } from 'src/app/domain/registration/models/IPartner';
import { WebsocketService } from './notification/websocket.service';
import { ChangePasswordModalComponent } from './change-password-modal/change-password-modal.component';
import { SelectBranchModalComponent } from './select-branch-modal/select-branch-modal.component';
import { ThemeService } from '../services/theme/theme.service';
import { ModuleCard, ModuleCatalogService } from '../services/module-catalog/module-catalog.service';
import { NotificationCrudIndex } from 'src/app/views/notifications-modal/crud-index/notification.component';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.sass'],
})
export class NavbarComponent implements OnInit {
  public user: IUser;
  public partners: IPartner;
  public menu: IMenu[] = [];
  unreadCount = 0;
  isSidebarCollapsed = false;

  /** Filiais ativas acessiveis ao usuario. Vazio = sem suporte ou 0/1 filial. */
  branchOptions: Array<{ id: string; name: string }> = [];
  /** Nome da filial atualmente selecionada, para exibicao no menu. */
  selectedBranchName: string | null = null;

  /** Indica se a rota atual é a home (onde a busca/tema já existem no cabeçalho). */
  public isHomeRoute = true;
  /** Termo digitado no campo de busca de módulos da navbar. */
  public moduleSearch = '';
  /** Estado atual do tema (para atualizar o ícone do botão). */
  public darkMode = false;
  /** Cards de módulos disponíveis para a busca da navbar. */
  private moduleCards: ModuleCard[] = [];

  @ViewChild('sidenav') sidenav: MatSidenav | undefined;
  @ViewChild('moduleSearchRef') moduleSearchRef: ElementRef<HTMLElement> | undefined;

  elem: any;
  notifications: any;

  constructor(
    public authService: AuthService,
    private router: Router,
    private toastService: ToastService,
    private barService: BarService,
    public keyboardService: KeyboardService,
    private websocketService: WebsocketService,
    private dialog: MatDialog,
    @Inject(DOCUMENT) private document: any,
    private themeService?: ThemeService,
    private moduleCatalog?: ModuleCatalogService
  ) {
    this.router.events.subscribe(() => {
      this.closeSideNav();
      const currentUser = this.authService.getCurrentUser();
      if (currentUser) {
        this.user = currentUser;
        this.partners = this.authService.getPartner();
      }
    });
    const currentUser = this.authService.getCurrentUser();
    if (currentUser) {
      this.user = currentUser;
    }

    this.updateHomeRouteFlag(this.router.url);
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.updateHomeRouteFlag(event.urlAfterRedirects));
  }

  ngOnInit(): void {
    this.elem = document.documentElement;
    this.loadMenu();
    this.loadBranchOptions();
    this.darkMode = this.themeService?.isDarkTheme() ?? false;
    this.moduleCards = this.moduleCatalog?.getNavigableCards() ?? [];
  }

  /** Cards filtrados pelo termo digitado no campo de busca da navbar. */
  public get filteredModuleCards(): ModuleCard[] {
    if (!this.moduleCatalog) {
      return [];
    }
    return this.moduleCatalog.filter(this.moduleCards, this.moduleSearch);
  }

  public setModuleSearch(value: string): void {
    this.moduleSearch = value;
  }

  public clearModuleSearch(): void {
    this.moduleSearch = '';
  }

  public toggleTheme(): void {
    if (!this.themeService) {
      return;
    }
    this.darkMode = this.themeService.toggle();
  }

  public navigateToModule(card: ModuleCard): void {
    this.clearModuleSearch();

    if (card.action) {
      if (card.action === 'openNotificationsModal') {
        this.openNotificationsModal();
      }
      return;
    }

    if (card.role === 'MENU_BIRTHDAYS') {
      this.router.navigate([card.routerLink], { queryParams: { tab: 'birthdays' } });
      return;
    }

    if (card.routerLink) {
      this.router.navigate([card.routerLink]);
    }
  }

  private openNotificationsModal(): void {
    this.dialog.open(NotificationCrudIndex, {
      width: '40%',
      disableClose: false,
    });
  }

  private updateHomeRouteFlag(url: string): void {
    const normalized = (url || '').split('?')[0].split('#')[0];
    this.isHomeRoute = normalized === '/' || normalized === '' || normalized.startsWith('/home-cards');
  }

  @HostListener('document:mousedown', ['$event'])
  public handleDocumentClick(event: MouseEvent): void {
    if (!this.moduleSearch) {
      return;
    }
    const target = event.target as Node | null;
    const container = this.moduleSearchRef?.nativeElement;
    if (target && container && !container.contains(target)) {
      this.clearModuleSearch();
    }
  }

  @HostListener('document:keydown.escape')
  public handleEscape(): void {
    if (this.moduleSearch) {
      this.clearModuleSearch();
    }
  }

  private loadBranchOptions(): void {
    this.authService
      .listBranchOptions()
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.branchOptions = res?.options || [];
          if (res?.selectedBranchId) {
            const found = this.branchOptions.find((b) => b.id === res.selectedBranchId);
            this.selectedBranchName = found?.name || null;
          }
        },
        error: () => {
          // Silencioso: sem suporte a filiais neste tenant
          this.branchOptions = [];
        },
      });
  }

  private loadMenu(): void {
    this.barService.getNavbarMenus().subscribe((result: IMenu[]) => {
      this.menu = this.processMenuItems(result);
    });
  }

  private processMenuItems(items: IMenu[]): IMenu[] {
    const processMenuItem = (item: IMenu): IMenu | null => {
      // Itens do tipo "section" são apenas subtítulos visuais no menu lateral.
      // Não passam por filtro de permissão nem possuem filhos/rotas.
      if (item.type === 'section') {
        return {
          role: item.role,
          show: true,
          enable: true,
          text: item.text,
          icon: item.icon,
          routerLink: item.routerLink,
          children: [],
          type: 'section',
        };
      }

      const children = item.children || [];
      const processedChildren =
        children.length > 0
          ? children
              .map((child) => processMenuItem(child))
              .filter((child): child is IMenu => child !== null)
          : [];

      const hasShowPermission =
        this.authService.isShow(item) || processedChildren.length > 0;

      if (!hasShowPermission) {
        return null;
      }

      const hasEnablePermission =
        this.authService.isEnable(item) ||
        processedChildren.some((child) => child.enable);

      return {
        role: item.role,
        show: true,
        enable: hasEnablePermission,
        text: item.text,
        icon: item.icon,
        routerLink: item.routerLink,
        children: processedChildren,
      };
    };

    return items
      .map(item => processMenuItem(item))
      .filter((item): item is IMenu => item !== null);
  }

  getDisplayName(): string {
    // Se for parceiro, prioriza name, depois companyName
    if (this.partners) {
      if (this.partners.name && this.partners.name.trim() !== '') {
        return this.partners.name;
      }
      if (this.partners.companyName && this.partners.companyName.trim() !== '') {
        return this.partners.companyName;
      }
    }
    // Se não for parceiro, exibe o firstName do usuário
    return this.user?.firstName || 'Usuário';
  }

  getTenantCompanySlug(): string {
    return (window.localStorage.getItem('@gmt:login_slug') || '').trim();
  }

  getTenantCompanyLabel(): string | null {
    const slug = this.getTenantCompanySlug();
    return slug ? `Empresa: ${slug}` : null;
  }

  getProfileTooltip(): string {
    const displayName = this.getDisplayName();
    const companyLabel = this.getTenantCompanyLabel();
    return companyLabel ? `${displayName} - ${companyLabel}` : displayName;
  }

  getImageUrl(): string {
    const avatar = this.user.avatarUrl || 'user_img_default.png';
    return `${S3_URL_BASE}${avatar}`;
  }

  logout() {
    const finishLogout = () => {
      window.localStorage.removeItem('@gmt:access_token');
      window.localStorage.removeItem('@gmt:refresh_token');
      window.localStorage.removeItem('@gmt:tenant_id');
      window.localStorage.removeItem('@gmt:login_slug');
      window.localStorage.removeItem('@gmt:user');
      window.localStorage.removeItem('@gmt:permissions');
      this.router.navigate(['login']);
    };

    this.authService
      .logout()
      .pipe(take(1))
      .subscribe(
        (res: any) => {
          if (res?.affected > 0) {
            console.debug('Token removido com sucesso!');
          }
          finishLogout();
        },
        (err) => {
          finishLogout();
          this.toastService.showError(err);
        }
      );
  }

  public openChangePassword(): void {
    this.dialog.open(ChangePasswordModalComponent, {
      width: '420px',
      disableClose: false,
    });
  }

  public openSelectBranch(): void {
    const dialogRef = this.dialog.open(SelectBranchModalComponent, {
      width: '400px',
      data: {
        branches: this.branchOptions,
        mandatory: false,
      },
    });

    dialogRef.afterClosed().pipe(take(1)).subscribe((selectedBranchId: string | null) => {
      if (selectedBranchId) {
        const found = this.branchOptions.find((b) => b.id === selectedBranchId);
        this.selectedBranchName = found?.name || null;
      }
    });
  }

  public closeSideNav() {
    this.sidenav?.close();
  }

  public toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  public get isFullScreen(): boolean {
    return !!(
      this.document.fullscreenElement ||
      this.document.webkitFullscreenElement ||
      this.document.mozFullScreenElement ||
      this.document.msFullscreenElement
    );
  }
  public openFullScreen() {
    if (this.elem.requestFullscreen) {
      this.elem.requestFullscreen();
    } else if (this.elem.mozRequestFullScreen) {
      this.elem.mozRequestFullScreen();
    } else if (this.elem.webkitRequestFullscreen) {
      this.elem.webkitRequestFullscreen();
    } else if (this.elem.msRequestFullscreen) {
      this.elem.msRequestFullscreen();
    }
  }

  public get isPossibleCloseFullScreen() {
    return (
      this.document.fullscreenElement ||
      this.document.webkitFullscreenElement ||
      this.document.mozFullScreenElement
    );
  }

  public closeFullScreen() {
    if (this.isPossibleCloseFullScreen) {
      this.document.exitFullscreen();
    } else if (this.document.mozCancelFullScreen) {
      this.document.mozCancelFullScreen();
    } else if (this.document.webkitExitFullscreen) {
      this.document.webkitExitFullscreen();
    } else if (this.document.msExitFullscreen) {
      this.document.msExitFullscreen();
    }
  }

  public clearCache() {
    this.toastService.showInfo('Iniciando a limpeza do cache...');

    caches.keys()
      .then((cacheNames) => {
        const deletePromises = cacheNames.map((cacheName) => caches.delete(cacheName));
        return Promise.all(deletePromises);
      })
      .then(() => {
        this.toastService.showInfo('Cache limpo com sucesso!');
        this.authService
          .logout()
          .pipe(take(1))
          .subscribe(
            () => {
              window.localStorage.removeItem('@gmt:access_token');
              window.localStorage.removeItem('@gmt:refresh_token');
              window.localStorage.removeItem('@gmt:tenant_id');
              window.localStorage.removeItem('@gmt:login_slug');
              window.localStorage.removeItem('@gmt:user');
              window.localStorage.removeItem('@gmt:permissions');
              this.router.navigate(['login']);
            },
            (err) => {
              console.error('Erro ao deslogar:', err);
              this.toastService.showError('Erro ao deslogar o usuário.');
            }
          );
      })
      .catch((error) => {
        console.error('Erro ao limpar o cache:', error);
        this.toastService.showError('Erro ao limpar o cache.');
      });
  }
}
